export type PieceType = 'K' | 'Q' | 'R' | 'B' | 'N' | 'P';
export type PieceColor = 'white' | 'black';
export type Piece = {
  type: PieceType;
  color: PieceColor;
} | null;

export type Board = Piece[][];
export type Position = [number, number];

export interface CastlingRights {
  whiteKingSide: boolean;
  whiteQueenSide: boolean;
  blackKingSide: boolean;
  blackQueenSide: boolean;
}

export interface GameState {
  board: Board;
  sideToMove: PieceColor;
  castlingRights: CastlingRights;
  enPassantTarget: Position | null;
  halfmoveClock: number;
  fullmoveNumber: number;
  moveHistory: string[];
  positionHistory: Map<string, number>;
}

export interface MoveResult {
  isLegal: boolean;
  newState?: GameState;
  reason?: string;
}

export interface GameStatus {
  status: 'ongoing' | 'check' | 'checkmate' | 'stalemate' | 'draw';
  reason?: string;
  winner?: PieceColor | null;
}

// Initial chess position
export const createInitialGameState = (): GameState => {
  const initialBoard: Board = [
    [
      { type: 'R', color: 'black' }, { type: 'N', color: 'black' }, { type: 'B', color: 'black' }, { type: 'Q', color: 'black' },
      { type: 'K', color: 'black' }, { type: 'B', color: 'black' }, { type: 'N', color: 'black' }, { type: 'R', color: 'black' }
    ],
    Array(8).fill(null).map(() => ({ type: 'P', color: 'black' })),
    Array(8).fill(null),
    Array(8).fill(null),
    Array(8).fill(null),
    Array(8).fill(null),
    Array(8).fill(null).map(() => ({ type: 'P', color: 'white' })),
    [
      { type: 'R', color: 'white' }, { type: 'N', color: 'white' }, { type: 'B', color: 'white' }, { type: 'Q', color: 'white' },
      { type: 'K', color: 'white' }, { type: 'B', color: 'white' }, { type: 'N', color: 'white' }, { type: 'R', color: 'white' }
    ]
  ];

  return {
    board: initialBoard,
    sideToMove: 'white',
    castlingRights: {
      whiteKingSide: true,
      whiteQueenSide: true,
      blackKingSide: true,
      blackQueenSide: true
    },
    enPassantTarget: null,
    halfmoveClock: 0,
    fullmoveNumber: 1,
    moveHistory: [],
    positionHistory: new Map()
  };
};

export const isValidPosition = (row: number, col: number): boolean => {
  return row >= 0 && row < 8 && col >= 0 && col < 8;
};

export const pieceToChar = (piece: Piece): string => {
  if (!piece) return '';
  const char = piece.type.toLowerCase();
  return piece.color === 'white' ? char.toUpperCase() : char;
};

export const charToPiece = (char: string): Piece => {
  if (!char || char === ' ') return null;
  const isWhite = char === char.toUpperCase();
  return {
    type: char.toUpperCase() as PieceType,
    color: isWhite ? 'white' : 'black'
  };
};

// Convert position to algebraic notation (e.g., [0,0] -> "a8")
export const positionToAlgebraic = (pos: Position): string => {
  const [row, col] = pos;
  return String.fromCharCode(97 + col) + (8 - row).toString();
};

// Convert algebraic notation to position (e.g., "a8" -> [0,0])
export const algebraicToPosition = (algebraic: string): Position => {
  const col = algebraic.charCodeAt(0) - 97;
  const row = 8 - parseInt(algebraic[1]);
  return [row, col];
};

// Get piece at position
export const getPieceAt = (board: Board, pos: Position): Piece => {
  const [row, col] = pos;
  if (!isValidPosition(row, col)) return null;
  return board[row][col];
};

// Check if square is attacked by opponent
export const isSquareAttacked = (board: Board, pos: Position, byColor: PieceColor): boolean => {
  const [targetRow, targetCol] = pos;

  for (let row = 0; row < 8; row++) {
    for (let col = 0; col < 8; col++) {
      const piece = board[row][col];
      if (!piece || piece.color !== byColor) continue;

      if (canPieceAttackSquare(board, [row, col], pos)) {
        return true;
      }
    }
  }
  return false;
};

// Check if a piece can attack a specific square (used for check detection)
const canPieceAttackSquare = (board: Board, from: Position, to: Position): boolean => {
  const [fromRow, fromCol] = from;
  const [toRow, toCol] = to;
  const piece = board[fromRow][fromCol];
  
  if (!piece) return false;

  const rowDiff = toRow - fromRow;
  const colDiff = toCol - fromCol;
  const absRowDiff = Math.abs(rowDiff);
  const absColDiff = Math.abs(colDiff);

  switch (piece.type) {
    case 'P': {
      const direction = piece.color === 'white' ? -1 : 1;
      // Pawn attacks diagonally
      return rowDiff === direction && absColDiff === 1;
    }
    case 'R': {
      if (rowDiff !== 0 && colDiff !== 0) return false;
      return isPathClear(board, from, to);
    }
    case 'N': {
      return (absRowDiff === 2 && absColDiff === 1) || (absRowDiff === 1 && absColDiff === 2);
    }
    case 'B': {
      if (absRowDiff !== absColDiff) return false;
      return isPathClear(board, from, to);
    }
    case 'Q': {
      if (rowDiff !== 0 && colDiff !== 0 && absRowDiff !== absColDiff) return false;
      return isPathClear(board, from, to);
    }
    case 'K': {
      return absRowDiff <= 1 && absColDiff <= 1;
    }
    default:
      return false;
  }
};

// Check if path between two squares is clear
export const isPathClear = (board: Board, from: Position, to: Position): boolean => {
  const [fromRow, fromCol] = from;
  const [toRow, toCol] = to;
  
  const rowStep = toRow > fromRow ? 1 : toRow < fromRow ? -1 : 0;
  const colStep = toCol > fromCol ? 1 : toCol < fromCol ? -1 : 0;
  
  let currentRow = fromRow + rowStep;
  let currentCol = fromCol + colStep;
  
  while (currentRow !== toRow || currentCol !== toCol) {
    if (board[currentRow][currentCol] !== null) {
      return false;
    }
    currentRow += rowStep;
    currentCol += colStep;
  }
  
  return true;
};

// Find king position
export const findKing = (board: Board, color: PieceColor): Position | null => {
  for (let row = 0; row < 8; row++) {
    for (let col = 0; col < 8; col++) {
      const piece = board[row][col];
      if (piece && piece.type === 'K' && piece.color === color) {
        return [row, col];
      }
    }
  }
  return null;
};

// Check if king is in check
export const isInCheck = (board: Board, color: PieceColor): boolean => {
  const kingPos = findKing(board, color);
  if (!kingPos) return false;
  
  return isSquareAttacked(board, kingPos, color === 'white' ? 'black' : 'white');
};

// Generate all pseudo-legal moves for a piece
export const getPseudoLegalMoves = (board: Board, from: Position): Position[] => {
  const piece = getPieceAt(board, from);
  if (!piece) return [];

  const [row, col] = from;
  const moves: Position[] = [];

  switch (piece.type) {
    case 'P':
      return getPawnMoves(board, from);
    case 'R':
      return getRookMoves(board, from);
    case 'N':
      return getKnightMoves(board, from);
    case 'B':
      return getBishopMoves(board, from);
    case 'Q':
      return getQueenMoves(board, from);
    case 'K':
      return getKingMoves(board, from);
    default:
      return [];
  }
};

const getPawnMoves = (board: Board, from: Position): Position[] => {
  const [row, col] = from;
  const piece = getPieceAt(board, from);
  if (!piece || piece.type !== 'P') return [];

  const moves: Position[] = [];
  const direction = piece.color === 'white' ? -1 : 1;
  const startRow = piece.color === 'white' ? 6 : 1;

  // Forward move
  const newRow = row + direction;
  if (isValidPosition(newRow, col) && !board[newRow][col]) {
    moves.push([newRow, col]);
    
    // Double move from starting position
    if (row === startRow && !board[newRow + direction][col]) {
      moves.push([newRow + direction, col]);
    }
  }

  // Captures
  [-1, 1].forEach(colOffset => {
    const newCol = col + colOffset;
    if (isValidPosition(newRow, newCol)) {
      const targetPiece = board[newRow][newCol];
      if (targetPiece && targetPiece.color !== piece.color) {
        moves.push([newRow, newCol]);
      }
    }
  });

  return moves;
};

const getRookMoves = (board: Board, from: Position): Position[] => {
  return getSlidingMoves(board, from, [[0, 1], [0, -1], [1, 0], [-1, 0]]);
};

const getBishopMoves = (board: Board, from: Position): Position[] => {
  return getSlidingMoves(board, from, [[1, 1], [1, -1], [-1, 1], [-1, -1]]);
};

const getQueenMoves = (board: Board, from: Position): Position[] => {
  return getSlidingMoves(board, from, [
    [0, 1], [0, -1], [1, 0], [-1, 0],
    [1, 1], [1, -1], [-1, 1], [-1, -1]
  ]);
};

const getSlidingMoves = (board: Board, from: Position, directions: number[][]): Position[] => {
  const [row, col] = from;
  const piece = getPieceAt(board, from);
  if (!piece) return [];

  const moves: Position[] = [];

  directions.forEach(([dRow, dCol]) => {
    for (let i = 1; i < 8; i++) {
      const newRow = row + dRow * i;
      const newCol = col + dCol * i;
      
      if (!isValidPosition(newRow, newCol)) break;
      
      const targetPiece = board[newRow][newCol];
      if (!targetPiece) {
        moves.push([newRow, newCol]);
      } else {
        if (targetPiece.color !== piece.color) {
          moves.push([newRow, newCol]);
        }
        break;
      }
    }
  });

  return moves;
};

const getKnightMoves = (board: Board, from: Position): Position[] => {
  const [row, col] = from;
  const piece = getPieceAt(board, from);
  if (!piece) return [];

  const moves: Position[] = [];
  const knightMoves = [
    [-2, -1], [-2, 1], [-1, -2], [-1, 2],
    [1, -2], [1, 2], [2, -1], [2, 1]
  ];

  knightMoves.forEach(([dRow, dCol]) => {
    const newRow = row + dRow;
    const newCol = col + dCol;
    
    if (isValidPosition(newRow, newCol)) {
      const targetPiece = board[newRow][newCol];
      if (!targetPiece || targetPiece.color !== piece.color) {
        moves.push([newRow, newCol]);
      }
    }
  });

  return moves;
};

const getKingMoves = (board: Board, from: Position): Position[] => {
  const [row, col] = from;
  const piece = getPieceAt(board, from);
  if (!piece) return [];

  const moves: Position[] = [];
  const directions = [
    [-1, -1], [-1, 0], [-1, 1],
    [0, -1],           [0, 1],
    [1, -1],  [1, 0],  [1, 1]
  ];

  directions.forEach(([dRow, dCol]) => {
    const newRow = row + dRow;
    const newCol = col + dCol;
    
    if (isValidPosition(newRow, newCol)) {
      const targetPiece = board[newRow][newCol];
      if (!targetPiece || targetPiece.color !== piece.color) {
        moves.push([newRow, newCol]);
      }
    }
  });

  return moves;
};

// Get all legal moves including special moves
export const getAllLegalMoves = (gameState: GameState): { from: Position; to: Position; promotion?: PieceType }[] => {
  const moves: { from: Position; to: Position; promotion?: PieceType }[] = [];

  for (let row = 0; row < 8; row++) {
    for (let col = 0; col < 8; col++) {
      const piece = gameState.board[row][col];
      if (!piece || piece.color !== gameState.sideToMove) continue;

      const from: Position = [row, col];
      const pseudoMoves = getPseudoLegalMoves(gameState.board, from);

      for (const to of pseudoMoves) {
        const moveResult = validateMove(gameState, from, to);
        if (moveResult.isLegal) {
          // Handle pawn promotion
          if (piece.type === 'P' && (to[0] === 0 || to[0] === 7)) {
            ['Q', 'R', 'B', 'N'].forEach(promotionPiece => {
              moves.push({ from, to, promotion: promotionPiece as PieceType });
            });
          } else {
            moves.push({ from, to });
          }
        }
      }

      // Add castling moves
      if (piece.type === 'K') {
        const castlingMoves = getCastlingMoves(gameState, from);
        moves.push(...castlingMoves.map(to => ({ from, to })));
      }

      // Add en passant moves
      if (piece.type === 'P' && gameState.enPassantTarget) {
        const enPassantMove = getEnPassantMove(gameState, from);
        if (enPassantMove) {
          moves.push({ from, to: enPassantMove });
        }
      }
    }
  }

  return moves;
};

// Validate and execute a move
export const validateMove = (
  gameState: GameState, 
  from: Position, 
  to: Position, 
  promotion?: PieceType
): MoveResult => {
  const [fromRow, fromCol] = from;
  const [toRow, toCol] = to;
  
  if (!isValidPosition(fromRow, fromCol) || !isValidPosition(toRow, toCol)) {
    return { isLegal: false, reason: 'Invalid position' };
  }

  const piece = getPieceAt(gameState.board, from);
  if (!piece) {
    return { isLegal: false, reason: 'No piece at from position' };
  }

  if (piece.color !== gameState.sideToMove) {
    return { isLegal: false, reason: 'Not your piece' };
  }

  // Check if it's a pseudo-legal move
  const pseudoMoves = getPseudoLegalMoves(gameState.board, from);
  const isPseudoLegal = pseudoMoves.some(([r, c]) => r === toRow && c === toCol);
  
  if (!isPseudoLegal) {
    // Check special moves
    if (piece.type === 'K') {
      const castlingMoves = getCastlingMoves(gameState, from);
      if (!castlingMoves.some(([r, c]) => r === toRow && c === toCol)) {
        return { isLegal: false, reason: 'Invalid move for this piece' };
      }
    } else if (piece.type === 'P' && gameState.enPassantTarget) {
      const enPassantMove = getEnPassantMove(gameState, from);
      if (!enPassantMove || enPassantMove[0] !== toRow || enPassantMove[1] !== toCol) {
        return { isLegal: false, reason: 'Invalid move for this piece' };
      }
    } else {
      return { isLegal: false, reason: 'Invalid move for this piece' };
    }
  }

  // Create new board state
  const newBoard = gameState.board.map(row => [...row]);
  
  // Handle special moves
  let isCapture = false;
  let isCastling = false;
  let isEnPassant = false;
  
  // Handle castling
  if (piece.type === 'K' && Math.abs(toCol - fromCol) === 2) {
    isCastling = true;
    // Move rook
    if (toCol === 6) { // King-side castling
      newBoard[fromRow][5] = newBoard[fromRow][7];
      newBoard[fromRow][7] = null;
    } else { // Queen-side castling
      newBoard[fromRow][3] = newBoard[fromRow][0];
      newBoard[fromRow][0] = null;
    }
  }
  
  // Handle en passant
  if (piece.type === 'P' && gameState.enPassantTarget && 
      toRow === gameState.enPassantTarget[0] && toCol === gameState.enPassantTarget[1]) {
    isEnPassant = true;
    isCapture = true;
    // Remove the captured pawn
    const capturedPawnRow = piece.color === 'white' ? toRow + 1 : toRow - 1;
    newBoard[capturedPawnRow][toCol] = null;
  }
  
  // Handle regular capture
  if (newBoard[toRow][toCol] !== null) {
    isCapture = true;
  }
  
  // Move the piece
  newBoard[toRow][toCol] = piece;
  newBoard[fromRow][fromCol] = null;
  
  // Handle pawn promotion
  if (piece.type === 'P' && (toRow === 0 || toRow === 7)) {
    if (!promotion) promotion = 'Q'; // Default to queen
    newBoard[toRow][toCol] = { type: promotion, color: piece.color };
  }

  // Check if this move leaves own king in check
  if (isInCheck(newBoard, piece.color)) {
    return { isLegal: false, reason: 'Move leaves king in check' };
  }

  // Update game state
  const newState: GameState = {
    ...gameState,
    board: newBoard,
    sideToMove: gameState.sideToMove === 'white' ? 'black' : 'white',
    halfmoveClock: isCapture || piece.type === 'P' ? 0 : gameState.halfmoveClock + 1,
    fullmoveNumber: gameState.sideToMove === 'black' ? gameState.fullmoveNumber + 1 : gameState.fullmoveNumber,
    enPassantTarget: null,
    castlingRights: { ...gameState.castlingRights },
    moveHistory: [...gameState.moveHistory],
    positionHistory: gameState.positionHistory instanceof Map 
      ? new Map(gameState.positionHistory)
      : new Map(Object.entries(gameState.positionHistory || {}))
  };

  // Update en passant target
  if (piece.type === 'P' && Math.abs(toRow - fromRow) === 2) {
    newState.enPassantTarget = [(fromRow + toRow) / 2, fromCol];
  }

  // Update castling rights
  if (piece.type === 'K') {
    if (piece.color === 'white') {
      newState.castlingRights.whiteKingSide = false;
      newState.castlingRights.whiteQueenSide = false;
    } else {
      newState.castlingRights.blackKingSide = false;
      newState.castlingRights.blackQueenSide = false;
    }
  }
  
  if (piece.type === 'R') {
    if (piece.color === 'white') {
      if (fromCol === 0) newState.castlingRights.whiteQueenSide = false;
      if (fromCol === 7) newState.castlingRights.whiteKingSide = false;
    } else {
      if (fromCol === 0) newState.castlingRights.blackQueenSide = false;
      if (fromCol === 7) newState.castlingRights.blackKingSide = false;
    }
  }

  // Update position history for repetition detection
  const positionKey = getPositionKey(newState);
  const count = newState.positionHistory.get(positionKey) || 0;
  newState.positionHistory.set(positionKey, count + 1);

  // Add move to history
  const moveNotation = `${positionToAlgebraic(from)}${positionToAlgebraic(to)}${promotion ? promotion.toLowerCase() : ''}`;
  newState.moveHistory.push(moveNotation);

  return { isLegal: true, newState };
};

// Get castling moves
const getCastlingMoves = (gameState: GameState, kingPos: Position): Position[] => {
  const moves: Position[] = [];
  const [row, col] = kingPos;
  const piece = getPieceAt(gameState.board, kingPos);
  
  if (!piece || piece.type !== 'K' || isInCheck(gameState.board, piece.color)) {
    return moves;
  }

  const canCastleKingSide = piece.color === 'white' ? 
    gameState.castlingRights.whiteKingSide : gameState.castlingRights.blackKingSide;
  const canCastleQueenSide = piece.color === 'white' ? 
    gameState.castlingRights.whiteQueenSide : gameState.castlingRights.blackQueenSide;

  // King-side castling
  if (canCastleKingSide && 
      !gameState.board[row][5] && !gameState.board[row][6] &&
      !isSquareAttacked(gameState.board, [row, 5], piece.color === 'white' ? 'black' : 'white') &&
      !isSquareAttacked(gameState.board, [row, 6], piece.color === 'white' ? 'black' : 'white')) {
    moves.push([row, 6]);
  }

  // Queen-side castling
  if (canCastleQueenSide && 
      !gameState.board[row][1] && !gameState.board[row][2] && !gameState.board[row][3] &&
      !isSquareAttacked(gameState.board, [row, 2], piece.color === 'white' ? 'black' : 'white') &&
      !isSquareAttacked(gameState.board, [row, 3], piece.color === 'white' ? 'black' : 'white')) {
    moves.push([row, 2]);
  }

  return moves;
};

// Get en passant move
const getEnPassantMove = (gameState: GameState, pawnPos: Position): Position | null => {
  if (!gameState.enPassantTarget) return null;
  
  const [row, col] = pawnPos;
  const piece = getPieceAt(gameState.board, pawnPos);
  
  if (!piece || piece.type !== 'P') return null;
  
  const [targetRow, targetCol] = gameState.enPassantTarget;
  
  // Check if pawn can capture en passant
  if (Math.abs(col - targetCol) === 1 && 
      ((piece.color === 'white' && row === 3 && targetRow === 2) ||
       (piece.color === 'black' && row === 4 && targetRow === 5))) {
    return gameState.enPassantTarget;
  }
  
  return null;
};

// Get position key for repetition detection
const getPositionKey = (gameState: GameState): string => {
  const boardStr = gameState.board.map(row => 
    row.map(piece => piece ? pieceToChar(piece) : '').join('')
  ).join('');
  
  return `${boardStr}_${gameState.sideToMove}_${JSON.stringify(gameState.castlingRights)}_${JSON.stringify(gameState.enPassantTarget)}`;
};

// Get game status
export const getGameStatus = (gameState: GameState): GameStatus => {
  const legalMoves = getAllLegalMoves(gameState);
  const inCheck = isInCheck(gameState.board, gameState.sideToMove);

  // Checkmate
  if (legalMoves.length === 0 && inCheck) {
    return {
      status: 'checkmate',
      winner: gameState.sideToMove === 'white' ? 'black' : 'white',
      reason: 'Checkmate'
    };
  }

  // Stalemate
  if (legalMoves.length === 0 && !inCheck) {
    return {
      status: 'stalemate',
      reason: 'Stalemate - no legal moves'
    };
  }

  // Check for draws
  // Insufficient material
  if (hasInsufficientMaterial(gameState.board)) {
    return {
      status: 'draw',
      reason: 'Insufficient material'
    };
  }

  // 75-move rule (automatic)
  if (gameState.halfmoveClock >= 150) {
    return {
      status: 'draw',
      reason: '75-move rule'
    };
  }

  // Fivefold repetition (automatic)
  const positionKey = getPositionKey(gameState);
  if (gameState.positionHistory.get(positionKey) >= 5) {
    return {
      status: 'draw',
      reason: 'Fivefold repetition'
    };
  }

  // Check if in check
  if (inCheck) {
    return {
      status: 'check',
      reason: 'King is in check'
    };
  }

  return { status: 'ongoing' };
};

// Check for insufficient material
const hasInsufficientMaterial = (board: Board): boolean => {
  const pieces: { [key: string]: number } = {};
  
  for (let row = 0; row < 8; row++) {
    for (let col = 0; col < 8; col++) {
      const piece = board[row][col];
      if (piece) {
        const key = `${piece.color}_${piece.type}`;
        pieces[key] = (pieces[key] || 0) + 1;
      }
    }
  }

  const totalPieces = Object.values(pieces).reduce((sum, count) => sum + count, 0);

  // King vs King
  if (totalPieces === 2) return true;

  // King + Bishop/Knight vs King
  if (totalPieces === 3) {
    return pieces['white_B'] === 1 || pieces['black_B'] === 1 ||
           pieces['white_N'] === 1 || pieces['black_N'] === 1;
  }

  // King + Bishop vs King + Bishop (same color squares)
  if (totalPieces === 4 && pieces['white_B'] === 1 && pieces['black_B'] === 1) {
    // This would require checking if bishops are on same color squares
    // Simplified for now
    return false;
  }

  return false;
};

// Make a move (convenience function)
export const makeMove = (
  gameState: GameState, 
  from: Position, 
  to: Position, 
  promotion?: PieceType
): GameState | null => {
  const result = validateMove(gameState, from, to, promotion);
  return result.isLegal ? result.newState! : null;
};